import { useEffect } from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import {
  classReportDetails,
  readingCohorts,
  resultMetricExplaination,
} from "../../utils/CommonUtils/constants";
import { getCurrentDate } from "../../utils/helper";
import useParamValues from "../../utils/useParamValues";
import html2pdf from "html2pdf.js";
import girlHoldingPhoneWordCloud from "../../assets/girl_holding_phone_word_cloud.svg";
import goldMedalImg from "../../assets/gold-medal-img.svg";
import CohortLevelBarSection from "../../components/CohortLevelBar/CohortLevelBarSection";
import CohortisedStudentList from "./cohortisedStudentList/CohortisedStudentList";
import useTranslationSetup from "../../utils/useTranslationSetup";
import "./styles.css";
import useWebViewBridge from "../../utils/useWebViewBridge";

const ClassReportCardPdf = ({
  showPdfContent,
  setShowPdfContent,
  students,
  paginatedPages,
  categories,
}) => {
  const { t } = useTranslationSetup();
  const classReportInfo = classReportDetails(
    t,
    students.length,
    useParamValues()
  );
  const { sendMessageToReactNative } = useWebViewBridge();

  const downloadPdfContent = () => {
    const element = document.getElementById("pdf-content");
    html2pdf()
      .set({
        margin: 0.1,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { dpi: 192, letterRendering: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { after: ".pdf-page" },
        filename: "class-report-card.pdf",
      })
      .from(element)
      .outputPdf("datauristring")
      .then((dataUri) => {
        const base64 = dataUri.split(",")[1];
        sendMessageToReactNative(
          JSON.stringify({ type: "download_pdf", data: base64 })
        );
      });

    setShowPdfContent(false);
  };

  useEffect(() => {
    if (showPdfContent) {
      setTimeout(() => {
        downloadPdfContent();
      }, 100);
    }
  }, [showPdfContent]);

  return (
    <Box id="pdf-content">
      {/* *********************************** First page of pdf ********************************** */}
      <Box className="pdf-page">
        <Box className="report-card-pdf-container">
          <Typography className="current-date-text">
            {`${t("date")}: ${getCurrentDate()}`}
          </Typography>
          <Box className="girl-holding-phone-word-cloud-img-headers-container">
            <img
              src={girlHoldingPhoneWordCloud}
              alt="girl-holding-phone-word-cloud"
              className="girl-holding-phone-word-cloud-img"
            />

            <Typography className="report-card-pdf-header">
              {t("shala_darpan_app")}
            </Typography>
            <Typography className="report-card-pdf-sub-header">
              {`${t("orf_performance_assessment", {
                assessmentNumber: 1,
              })}`}
            </Typography>
          </Box>

          <Box className="class-report-details-container">
            <Grid className="class-report-details-grid" container spacing={2}>
              {classReportInfo.map((item, index) => (
                <Grid
                  className="class-report-details-grid-item"
                  item
                  xs={6}
                  key={index}
                >
                  <Typography className="class-report-details-grid-item-label">
                    {item.label}
                  </Typography>
                  <Typography className="class-report-details-grid-item-value">
                    {item.value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box className="class-report-participation-congrats-container">
            <Typography className="class-report-participation-congrats-header">
              {t("many_congratulations")}
            </Typography>
            <Box className="class-report-participation-congrats-img-description-container">
              <Box className="gold-medal-img-container">
                <img src={goldMedalImg} alt="gold-medal-img" />
              </Box>
              <Typography className="class-report-participation-congrats-description">
                {t("orf_assessment_participation_message")}
              </Typography>
            </Box>
          </Box>

          <Box className="result-metric-explaination-container">
            <Typography className="result-metric-explaination-header">
              {t("understand_performance_indicators")}
            </Typography>
            {Object.entries(resultMetricExplaination(t)).map(([key, value]) => (
              <Box className="result-metric-img-title-description" key={key}>
                <img
                  className={`result-metric-img bcg-${key}`}
                  src={value.icon}
                  alt="result-metric-img"
                />
                <Box className="result-metric-title-description">
                  <Typography className="result-metric-title">
                    {value.title}
                  </Typography>
                  <Typography className="result-metric-description">
                    {value.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box className="explore-reading-cohorts-container">
            <Typography className="explore-reading-cohorts-header">
              {t("understand_reading_groups")}
            </Typography>
            <Box className="reading-cohort-details-container">
              {readingCohorts(t).map((item, index) => (
                <Box className="reading-cohort-details" key={index}>
                  <Box className="reading-cohort-icon-name-container">
                    <img
                      className="reading-cohort-icon"
                      src={item.icon}
                      alt="reading-cohort-icon"
                    />
                    <Typography className="reading-cohort-name">
                      {item.label}
                    </Typography>
                  </Box>
                  <Divider
                    className={`reading-cohort-divider border-color-${index}`}
                  />
                  <Typography className="reading-cohort-description">
                    {item.description}
                  </Typography>
                </Box>
              ))}
            </Box>
            <CohortLevelBarSection
              showCohortMetric={false}
              categories={categories}
            />
          </Box>
        </Box>
      </Box>

      {/* *********************************** Page showing cohortised list of students ********************************** */}

      {paginatedPages.map((cohorts, pageIndex) => (
        <Box className="pdf-page" key={pageIndex}>
          <CohortisedStudentList
            cohorts={cohorts}
            pageIndex={pageIndex}
            classLength={students.length}
          />
        </Box>
      ))}
    </Box>
  );
};

export default ClassReportCardPdf;
